import { ArrowDownIcon, ArrowUpIcon, type LucideIcon } from "lucide-react"
import { chipValuesOf } from "./filter-bar.ts"
import type { Sort } from "./table-view.ts"

/*
 * The rules behind the compact PageToolbar and its View sheet, kept apart from the components so
 * they can be checked without a browser: whether View is offered, what a facet's row says is
 * chosen, how many filters are in force, what the Order by row says and which ways an order runs,
 * and where Back goes from each of the sheet's levels.
 */

/** What of a facet a row in the Filter sheet reads: its values, the chosen ones, and its own names. */
type Chosen = {
  values: readonly { id: string; label: string }[]
  chosen: readonly string[]
  labelOf?: (id: string) => string
}

/**
 * What a facet's row in the Filter sheet says is chosen: nothing, the one value ("Mine"), or the
 * first and how many more ("security +2"), as its chip names them.
 */
export function chosenSummaryOf({ values, chosen, labelOf }: Chosen): string | undefined {
  const [first, ...rest] = chipValuesOf(values, chosen, labelOf)
  if (!first) return undefined
  return rest.length === 0 ? first.label : `${first.label} +${rest.length}`
}

/**
 * How many filters are in force, which the compact Filter button counts: each facet with a value
 * chosen, and each chip for a view choice that narrows the list (My tickets' "Closed included").
 */
export function inForceOf(facets: readonly Chosen[], chips: readonly unknown[] = []) {
  return facets.filter(({ chosen }) => chosen.length > 0).length + chips.length
}

/**
 * Whether the compact toolbar offers View: for the view's own options, an order, Display's rows or
 * a toggle. Filters alone have Filter, which opens the same sheet at them, so a second button
 * leading there would only repeat it.
 */
export function offersView({
  orderBy,
  display,
  toggles = [],
}: {
  orderBy?: unknown
  display?: unknown
  toggles?: readonly unknown[]
}) {
  return orderBy !== undefined || display !== undefined || toggles.length > 0
}

/**
 * What the View sheet's Filter row says is in force, counted as the Filter button counts it: the
 * one filter by its field and value ("Priority: High", "Label: security +2") or its chip ("Closed
 * included"), how many once there are more ("2 filters"), or "None".
 */
export function filterSummaryOf(
  facets: readonly (Chosen & { label: string })[],
  chips: readonly { label: string }[] = [],
) {
  const count = inForceOf(facets, chips)
  if (count === 0) return "None"
  if (count > 1) return `${count} filters`
  const facet = facets.find(({ chosen }) => chosen.length > 0)
  return facet ? `${facet.label}: ${chosenSummaryOf(facet)}` : (chips[0]?.label ?? "None")
}

/**
 * What the View sheet's Order by row says is in force: the order, and which way it runs where that
 * can change ("Name, descending"); nothing for an order it doesn't offer.
 */
export function orderSummaryOf({
  value,
  options,
  direction,
}: {
  value: string
  options: readonly { value: string; label: string }[]
  direction?: { value: Sort["direction"] }
}) {
  const label = options.find((option) => option.value === value)?.label
  return label && direction ? `${label}, ${direction.value}` : label
}

/**
 * The two ways an order runs, as its Direction offers them on `compact`: rows in the View sheet,
 * each with its arrow, and a choice in a card's Display.
 */
export const directions = [
  { value: "ascending", label: "Ascending", icon: ArrowUpIcon },
  { value: "descending", label: "Descending", icon: ArrowDownIcon },
] as const satisfies readonly { value: Sort["direction"]; label: string; icon: LucideIcon }[]

/** The direction a Direction choice names, or none for a value it doesn't offer. */
export const directionOf = (value: string) =>
  directions.find((direction) => direction.value === value)?.value

/**
 * Where the View sheet stands: its first level (`view`), the facets (`filter`), one facet's values,
 * the orders (`order`), or one Display option's choices (`choice`, by its label).
 */
export type ViewLevel =
  | { at: "view" }
  | { at: "filter" }
  | { at: "facet"; id: string }
  | { at: "order" }
  | { at: "choice"; label: string }

/**
 * Where Back goes from `level`, in a sheet `opened` at the View or straight at the Filter (from the
 * toolbar's Filter button): a facet's values go back to the facets, and the facets, the orders and
 * a Display option's choices to the View's first level, unless the sheet opened at the facets,
 * which then have nothing to go back to. The first level has none.
 */
export function parentOf(level: ViewLevel, opened: "view" | "filter"): ViewLevel | undefined {
  switch (level.at) {
    case "view":
      return undefined
    case "filter":
      return opened === "view" ? { at: "view" } : undefined
    case "facet":
      return { at: "filter" }
    case "order":
    case "choice":
      return { at: "view" }
  }
}
